function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useRef, useLayoutEffect } from 'react';
import { Rollout } from 'app_api/projects/feature_flags/types';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { InputReact } from 'common/components/input';
import { SvgIconReact } from 'common/components/svg-icon';
import { useTranslation } from 'common/i18n/hooks';
import { CohortsBuilderReact } from 'common/query-builder/cohorts-query-builder';
import { SerializedCohort } from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import { CohortsQueryBuilderUpdate } from 'common/query-builder/cohorts-query-builder/types';
import { promptSaveCohort } from 'apps/users/src/components/users-table/users-header/util';
import { useFeatureFlagPageContext } from 'iron/apps/feature-flags/src/components/context';
import styles from './index.module.styl';
const SHOULD_SHOW_COHORT_PICKER = false; // we're hiding the picker until the backend is hooked up

interface FeatureFlagRolloutsProps {
  projectId: number;
  workspaceId: number;
  canUpdate?: boolean;
  rollouts: Rollout[];
  setRollouts: (rollouts: Rollout[]) => void;
}
export function FeatureFlagRollouts(_ref: FeatureFlagRolloutsProps) {
  let {
    projectId,
    workspaceId,
    canUpdate = false,
    rollouts,
    setRollouts
  }: FeatureFlagRolloutsProps = _ref;
  const i18n = useTranslation();
  const [editingRolloutNameIndex, setEditingRolloutNameIndex] = useState<number | null>(null);
  const handleCohortUpdate = (cohort: SerializedCohort, index: number) => {
    if (cohort) {
      const updatedRollouts = rollouts.map((rollout, i) => i === index ? _objectSpread(_objectSpread({}, rollout), {}, {
        cohort_definition: _objectSpread(_objectSpread({}, rollout.cohort_definition), {}, {
          cohort,
          all: false
        })
      }) : rollout);
      setRollouts(updatedRollouts);
    }
  };
  const handleRolloutPercentageChange = (value: string, index: number) => {
    // Convert percentage to decimal format (0-1)
    const percentValue = Math.min(Math.max(parseFloat(value) || 0, 0), 100) / 100;
    const updatedRollouts = rollouts.map((rollout, i) => i === index ? _objectSpread(_objectSpread({}, rollout), {}, {
      rollout_percentage: percentValue
    }) : rollout);
    setRollouts(updatedRollouts);
  };
  const handleRolloutNameChange = (name: string, index: number) => {
    const updatedRollouts = rollouts.map((rollout, i) => i === index ? _objectSpread(_objectSpread({}, rollout), {}, {
      name
    }) : rollout);
    setRollouts(updatedRollouts);
    setEditingRolloutNameIndex(null);
  };
  const handleAddAssignmentGroup = () => {
    const newRollout: Rollout = {
      name: "Production Users",
      rollout_percentage: 1.0,
      cohort_definition: {
        all: true
      }
    };
    const updatedRollouts = [...rollouts, newRollout];
    setRollouts(updatedRollouts);
  };
  const handleSaveAsCohort = (cohort: SerializedCohort, index: number) => {
    promptSaveCohort(projectId, workspaceId, undefined, i18n.t("feature-flags.page.rollouts.save-as-title"), true, cohort, savedCohort => {
      handleCohortUpdate(savedCohort.serialize(), index);
    });
  };
  return <div className={styles.rollouts} data-sentry-component="FeatureFlagRollouts" data-sentry-source-file="index.tsx">
      <h2 className={styles.rolloutsHeader}>{i18n.t("feature-flags.page.rollouts.title")}</h2>
      {rollouts.map((rollout, index) => <RolloutRow key={"".concat(rollout.name, "-").concat(index)} rollout={rollout} canUpdate={canUpdate} isEditing={editingRolloutNameIndex === index} handleStartEditingName={() => setEditingRolloutNameIndex(index)} handleStopEditingName={() => setEditingRolloutNameIndex(null)} handleCohortUpdate={cohort => handleCohortUpdate(cohort, index)} handleSaveAsCohort={cohort => handleSaveAsCohort(cohort, index)} handleRolloutNameChange={name => handleRolloutNameChange(name, index)} handleRolloutPercentageChange={value => handleRolloutPercentageChange(value, index)} handleDeleteRollout={() => {
      const updatedRollouts = rollouts.filter((_, i) => i !== index);
      setRollouts(updatedRollouts);
    }} projectId={projectId} workspaceId={workspaceId} />)}
      {canUpdate && SHOULD_SHOW_COHORT_PICKER && <div className={styles.rolloutActions}>
          <ButtonReact theme={ButtonTheme.Secondary} onClick={handleAddAssignmentGroup}>
            {i18n.t("feature-flags.page.rollouts.add-assignment-group")}
          </ButtonReact>
        </div>}
    </div>;
}
interface RolloutRowProps {
  rollout: Rollout;
  canUpdate: boolean;
  isEditing: boolean;
  handleStartEditingName: () => void;
  handleStopEditingName: () => void;
  handleCohortUpdate: (cohort: SerializedCohort) => void;
  handleSaveAsCohort: (cohort: SerializedCohort) => void;
  handleRolloutNameChange: (name: string) => void;
  handleRolloutPercentageChange: (value: string) => void;
  handleDeleteRollout: () => void;
  projectId: number;
  workspaceId: number;
}
function RolloutRow(_ref2: RolloutRowProps) {
  let {
    rollout,
    canUpdate,
    isEditing,
    handleStartEditingName,
    handleStopEditingName,
    handleCohortUpdate,
    handleSaveAsCohort,
    handleRolloutNameChange,
    handleRolloutPercentageChange,
    handleDeleteRollout,
    projectId,
    workspaceId
  }: RolloutRowProps = _ref2;
  const i18n = useTranslation();
  const [isEdited, setIsEdited] = useState(false); // keep track of ability to save cohort
  const {
    canEdit
  } = useFeatureFlagPageContext();
  const RolloutNameButton = (_ref3: {
    name: string;
  }) => {
    let {
      name
    }: {
      name: string;
    } = _ref3;
    return <h2 className={styles.rolloutName} onClick={() => canUpdate && handleStartEditingName()} style={{
      cursor: canUpdate ? "pointer" : "default"
    }} data-sentry-component="RolloutNameButton" data-sentry-source-file="index.tsx">
      {name}
    </h2>;
  };
  const RolloutNameInput = (_ref4: {
    name: string;
  }) => {
    let {
      name
    }: {
      name: string;
    } = _ref4;
    const inputRef = useRef(null);
    useLayoutEffect(() => {
      // @ts-ignore
      inputRef.current.focus();
    }, []);
    return <InputReact ref={inputRef} className={styles.rolloutNameInput} disabled={!canEdit} value={name} onBlur={(ev: TODO) => {
      ev.preventDefault();
      handleStopEditingName();
    }} onKeydown={(ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        handleStopEditingName();
      } else if (ev.key === "Enter") {
        ev.preventDefault();
        handleRolloutNameChange((ev.target as HTMLInputElement).value);
      }
    }} data-sentry-element="InputReact" data-sentry-component="RolloutNameInput" data-sentry-source-file="index.tsx" />;
  };
  return <div className={styles.rolloutConfigContainer} data-sentry-component="RolloutRow" data-sentry-source-file="index.tsx">
      {canUpdate && SHOULD_SHOW_COHORT_PICKER && <SvgIconReact className={styles.deleteRollout} icon='trashcan' onClick={() => handleDeleteRollout()} />}
      {isEditing ? <RolloutNameInput name={rollout.name || ""} /> : <RolloutNameButton name={rollout.name || ""} />}
      <div className={styles.rolloutConfigRow}>
        {SHOULD_SHOW_COHORT_PICKER && <>
            <CohortsBuilderReact cohort={rollout.cohort_definition.cohort} onCohortUpdate={(_ref5: CohortsQueryBuilderUpdate) => {
          let {
            cohort
          }: CohortsQueryBuilderUpdate = _ref5;
          handleCohortUpdate(cohort.serialize());
          setIsEdited(true);
        }} projectId={projectId} workspaceId={workspaceId} projectHasCohorts supportB2bCohorts nonCollapsible footerItems={[isEdited ? <ButtonReact key='save-as' theme={ButtonTheme.Tertiary} onClick={() => {
          const cohort = rollout.cohort_definition.cohort as SerializedCohort; // we need this cause the API type is too broad
          handleSaveAsCohort(cohort);
          setIsEdited(false);
        }}>
                    {i18n.t("common.save-as")}
                  </ButtonReact> : null]} />
            <div className={styles.arrowIconWrapper}>
              <SvgIconReact icon='arrow-right' />
            </div>
          </>}
        <div className={styles.rolloutBox}>
          <div className={styles.rolloutTitle}>{i18n.t("feature-flags.page.rollouts.rollout-title")}</div>
          <div className={styles.percentageContainer}>
            <InputReact className={styles.percentageInput} disabled={!canEdit} value={String(Math.round(rollout.rollout_percentage * 100))} onChange={(ev: TODO) => handleRolloutPercentageChange(ev.detail.value)} type='number' min='0' max='100' data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
            %<span className={styles.enabledLabel}>{i18n.t("feature-flags.page.rollouts.enabled")}</span>
          </div>
        </div>
      </div>
    </div>;
}